import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Tab,
  Tabs,
  TextField,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import Header from "../../components/Header";
import IconsBar from "../../components/IconsBar/IconsBar";
import styled from "styled-components";
import { AuthContext } from "../../contexts/Auth/AuthContext";
import { staffCanAccessMenu } from "../../config/menuAccessCatalog";
import {
  createQuestionnaire,
  duplicateQuestionnaire,
  getQuestionnaireById,
  listQuestionnairesWithMeta,
  updateQuestionnaire,
  updateQuestionnaireSchema,
} from "../../api/endpoints/questionnaires";
import {
  getQuestionnaireTemplate,
  QUESTIONNAIRE_TEMPLATE_OPTIONS,
  slugifyTitle,
  statusColor,
  statusLabel,
} from "../../utils/questionnaireTemplates";

const Container = styled.div`
  min-height: 100vh;
  width: 100%;
  display: flex;
  flex-direction: column;
  background: linear-gradient(135deg, #6a1b9a 0%, #4527a0 40%, #f5f7fb 100%);
`;

const ContentArea = styled.div`
  width: 100%;
  max-width: 1100px;
  margin: 16px auto 24px;
  padding: 20px 16px 28px;
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.96);
  box-shadow: 0 18px 40px rgba(15, 23, 42, 0.16);
`;

function TabPanel({ children, value, index }) {
  if (value !== index) return null;
  return <Box sx={{ pt: 2 }}>{children}</Box>;
}

function formatDateInput(value) {
  if (!value) return "";
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "";
    return d.toISOString().slice(0, 16);
  } catch {
    return "";
  }
}

function SchemaPreview({ schema }) {
  const items = useMemo(() => {
    if (!schema) return [];
    if (schema.mode === "per_teacher") {
      return [
        ...(schema.criteria || []).map((c) => `[Critério] ${c.text}`),
        schema.observation ? `[Obs] ${schema.observation.text}` : null,
      ].filter(Boolean);
    }
    return (schema.sections || []).flatMap((section) =>
      (section.questions || []).map((q) => `[${section.title}] ${q.text}`)
    );
  }, [schema]);

  if (items.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        Nenhuma pergunta no schema.
      </Typography>
    );
  }

  return (
    <Box component="ul" sx={{ pl: 2, m: 0 }}>
      {items.map((text, idx) => (
        <Typography component="li" variant="body2" key={idx} sx={{ mb: 0.5 }}>
          {text}
        </Typography>
      ))}
    </Box>
  );
}

export default function QuestionnaireAdmin() {
  const { user } = useContext(AuthContext);
  const canManage = staffCanAccessMenu(user, "questionnaires_admin");

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState({
    title: "",
    slug: "",
    school_year: new Date().getFullYear().toString(),
    template: "school",
  });

  const [editOpen, setEditOpen] = useState(false);
  const [editTab, setEditTab] = useState(0);
  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState({
    title: "",
    slug: "",
    school_year: "",
    status: "DRAFT",
    opens_at: "",
    closes_at: "",
    schema_editable: false,
    response_count: 0,
    enabled_classroom_count: 0,
    mode: "standard",
  });
  const [schemaText, setSchemaText] = useState("");
  const [schemaParseError, setSchemaParseError] = useState("");
  const [saving, setSaving] = useState(false);

  const [duplicateOpen, setDuplicateOpen] = useState(false);
  const [duplicateSource, setDuplicateSource] = useState(null);
  const [duplicateForm, setDuplicateForm] = useState({
    title: "",
    slug: "",
    school_year: "",
  });

  const loadList = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await listQuestionnairesWithMeta();
      setItems(res.questionnaires || []);
    } catch (e) {
      setError(
        e?.response?.data?.message ||
          e?.response?.data?.error ||
          "Não foi possível carregar os questionários."
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (canManage) loadList();
    else setLoading(false);
  }, [canManage, loadList]);

  const openEdit = async (id) => {
    setSaving(true);
    setError("");
    setMessage("");
    try {
      const res = await getQuestionnaireById(id);
      const q = res.questionnaire;
      setEditId(id);
      setEditForm({
        title: q.title || "",
        slug: q.slug || "",
        school_year: q.school_year || "",
        status: q.status || "DRAFT",
        opens_at: formatDateInput(q.opens_at),
        closes_at: formatDateInput(q.closes_at),
        schema_editable: Boolean(q.schema_editable),
        response_count: q.response_count || 0,
        enabled_classroom_count: q.enabled_classroom_count || 0,
        mode: q.mode || "standard",
      });
      setSchemaText(JSON.stringify(q.schema, null, 2));
      setSchemaParseError("");
      setEditTab(0);
      setEditOpen(true);
    } catch (e) {
      setError(e?.response?.data?.message || "Erro ao carregar questionário.");
    } finally {
      setSaving(false);
    }
  };

  const handleCreate = async () => {
    setSaving(true);
    setError("");
    setMessage("");
    try {
      const schema = getQuestionnaireTemplate(createForm.template);
      await createQuestionnaire({
        title: createForm.title,
        slug: createForm.slug,
        school_year: createForm.school_year,
        schema,
      });
      setMessage("Questionário criado como rascunho.");
      setCreateOpen(false);
      await loadList();
    } catch (e) {
      const details = e?.response?.data?.details;
      setError(
        Array.isArray(details) && details.length
          ? `${e?.response?.data?.message}: ${details.join("; ")}`
          : e?.response?.data?.message || "Erro ao criar questionário."
      );
    } finally {
      setSaving(false);
    }
  };

  const handleSaveMetadata = async () => {
    if (!editId) return;
    setSaving(true);
    setError("");
    setMessage("");
    try {
      await updateQuestionnaire(editId, {
        title: editForm.title,
        school_year: editForm.school_year || null,
        status: editForm.status,
        opens_at: editForm.opens_at || null,
        closes_at: editForm.closes_at || null,
      });
      setMessage("Dados salvos.");
      await loadList();
      await openEdit(editId);
    } catch (e) {
      setError(e?.response?.data?.message || "Erro ao salvar.");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveSchema = async () => {
    if (!editId) return;
    setSaving(true);
    setError("");
    setMessage("");
    try {
      let parsed;
      try {
        parsed = JSON.parse(schemaText);
        setSchemaParseError("");
      } catch {
        setSchemaParseError("JSON inválido. Corrija antes de salvar.");
        setSaving(false);
        return;
      }
      await updateQuestionnaireSchema(editId, parsed);
      setMessage("Schema atualizado.");
      await loadList();
      await openEdit(editId);
    } catch (e) {
      const details = e?.response?.data?.details;
      setError(
        Array.isArray(details) && details.length
          ? `${e?.response?.data?.message}: ${details.join("; ")}`
          : e?.response?.data?.message || "Erro ao salvar schema."
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDuplicate = async () => {
    if (!duplicateSource) return;
    setSaving(true);
    setError("");
    setMessage("");
    try {
      await duplicateQuestionnaire(duplicateSource.id, duplicateForm);
      setMessage("Questionário duplicado como rascunho.");
      setDuplicateOpen(false);
      await loadList();
    } catch (e) {
      setError(e?.response?.data?.message || "Erro ao duplicar.");
    } finally {
      setSaving(false);
    }
  };

  let parsedSchemaPreview = null;
  if (schemaText && !schemaParseError) {
    try {
      parsedSchemaPreview = JSON.parse(schemaText);
    } catch {
      // preview only when valid
    }
  }

  if (!canManage) {
    return (
      <Container>
        <Header />
        <IconsBar />
        <ContentArea>
          <Alert severity="warning">Você não tem permissão para gerenciar questionários.</Alert>
        </ContentArea>
      </Container>
    );
  }

  return (
    <Container>
      <Header />
      <IconsBar />
      <ContentArea>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
          <Box>
            <Typography variant="h5" fontWeight="bold">
              Gerenciar questionários
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Crie, edite e duplique questionários. O schema só pode ser alterado antes de ativar turmas ou receber respostas.
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              setCreateForm({
                title: "",
                slug: "",
                school_year: new Date().getFullYear().toString(),
                template: "school",
              });
              setCreateOpen(true);
            }}
          >
            Novo questionário
          </Button>
        </Box>

        {message && <Alert severity="success" sx={{ mb: 2 }}>{message}</Alert>}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Paper variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Título</TableCell>
                <TableCell>Slug</TableCell>
                <TableCell>Ano</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="center">Respostas</TableCell>
                <TableCell align="center">Turmas ativas</TableCell>
                <TableCell align="right">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7}>Carregando...</TableCell>
                </TableRow>
              ) : items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7}>Nenhum questionário cadastrado.</TableCell>
                </TableRow>
              ) : (
                items.map((q) => (
                  <TableRow key={q.id} hover>
                    <TableCell>{q.title}</TableCell>
                    <TableCell>
                      <Typography variant="caption" sx={{ fontFamily: "monospace" }}>
                        {q.slug}
                      </Typography>
                    </TableCell>
                    <TableCell>{q.school_year || "—"}</TableCell>
                    <TableCell>
                      <Chip size="small" label={statusLabel(q.status)} color={statusColor(q.status)} />
                    </TableCell>
                    <TableCell align="center">{q.response_count ?? 0}</TableCell>
                    <TableCell align="center">{q.enabled_classroom_count ?? 0}</TableCell>
                    <TableCell align="right">
                      <Tooltip title="Editar">
                        <IconButton size="small" onClick={() => openEdit(q.id)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Duplicar">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setDuplicateSource(q);
                            setDuplicateForm({
                              title: `${q.title} (cópia)`,
                              slug: `${q.slug}-copia`,
                              school_year: q.school_year || "",
                            });
                            setDuplicateOpen(true);
                          }}
                        >
                          <ContentCopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </Paper>

        <Alert severity="info" sx={{ mt: 2 }}>
          Para liberar no app: defina status <strong>Ativo</strong> e ative a turma em Avaliações.
          Relatórios ficam em Estatísticas → Questionários.
        </Alert>
      </ContentArea>

      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Novo questionário</DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
          <TextField
            label="Título"
            value={createForm.title}
            onChange={(e) => {
              const title = e.target.value;
              setCreateForm((prev) => ({
                ...prev,
                title,
                slug: prev.slug || slugifyTitle(title),
              }));
            }}
            fullWidth
          />
          <TextField
            label="Slug (identificador único)"
            value={createForm.slug}
            onChange={(e) => setCreateForm((prev) => ({ ...prev, slug: e.target.value }))}
            helperText="Ex: avaliacao-escola-2026-2s"
            fullWidth
          />
          <TextField
            label="Ano letivo"
            value={createForm.school_year}
            onChange={(e) => setCreateForm((prev) => ({ ...prev, school_year: e.target.value }))}
            fullWidth
          />
          <FormControl fullWidth>
            <InputLabel>Modelo inicial</InputLabel>
            <Select
              label="Modelo inicial"
              value={createForm.template}
              onChange={(e) => setCreateForm((prev) => ({ ...prev, template: e.target.value }))}
            >
              {QUESTIONNAIRE_TEMPLATE_OPTIONS.map((opt) => (
                <MenuItem key={opt.id} value={opt.id}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancelar</Button>
          <Button variant="contained" disabled={saving} onClick={handleCreate}>
            Criar rascunho
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Editar questionário</DialogTitle>
        <DialogContent>
          <Tabs value={editTab} onChange={(_, v) => setEditTab(v)} sx={{ mb: 1 }}>
            <Tab label="Geral" />
            <Tab label="Schema (JSON)" />
            <Tab label="Prévia" />
          </Tabs>

          <TabPanel value={editTab} index={0}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <TextField label="Slug" value={editForm.slug} disabled fullWidth />
              <TextField
                label="Título"
                value={editForm.title}
                onChange={(e) => setEditForm((p) => ({ ...p, title: e.target.value }))}
                fullWidth
              />
              <TextField
                label="Ano letivo"
                value={editForm.school_year}
                onChange={(e) => setEditForm((p) => ({ ...p, school_year: e.target.value }))}
                fullWidth
              />
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  label="Status"
                  value={editForm.status}
                  onChange={(e) => setEditForm((p) => ({ ...p, status: e.target.value }))}
                >
                  <MenuItem value="DRAFT">Rascunho</MenuItem>
                  <MenuItem value="ACTIVE">Ativo</MenuItem>
                  <MenuItem value="CLOSED">Encerrado</MenuItem>
                </Select>
              </FormControl>
              <TextField
                label="Abre em"
                type="datetime-local"
                value={editForm.opens_at}
                onChange={(e) => setEditForm((p) => ({ ...p, opens_at: e.target.value }))}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
              <TextField
                label="Encerra em"
                type="datetime-local"
                value={editForm.closes_at}
                onChange={(e) => setEditForm((p) => ({ ...p, closes_at: e.target.value }))}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
              <Typography variant="caption" color="text.secondary">
                Respostas: {editForm.response_count} · Turmas ativas: {editForm.enabled_classroom_count} ·
                Modo: {editForm.mode === "per_teacher" ? "Por professor" : "Padrão"}
              </Typography>
            </Box>
          </TabPanel>

          <TabPanel value={editTab} index={1}>
            {!editForm.schema_editable && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                Schema bloqueado: já há respostas ou turmas com questionário ativo. Duplique para criar uma nova versão.
              </Alert>
            )}
            <TextField
              multiline
              minRows={14}
              fullWidth
              value={schemaText}
              disabled={!editForm.schema_editable}
              onChange={(e) => {
                const value = e.target.value;
                setSchemaText(value);
                try {
                  JSON.parse(value);
                  setSchemaParseError("");
                } catch {
                  setSchemaParseError("JSON inválido");
                }
              }}
              sx={{ fontFamily: "monospace", fontSize: 13 }}
            />
            {schemaParseError && (
              <Typography variant="caption" color="error">
                {schemaParseError}
              </Typography>
            )}
          </TabPanel>

          <TabPanel value={editTab} index={2}>
            <SchemaPreview schema={parsedSchemaPreview} />
          </TabPanel>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>Fechar</Button>
          {editTab === 0 && (
            <Button variant="contained" disabled={saving} onClick={handleSaveMetadata}>
              Salvar dados
            </Button>
          )}
          {editTab === 1 && editForm.schema_editable && (
            <Button variant="contained" disabled={saving || Boolean(schemaParseError)} onClick={handleSaveSchema}>
              Salvar schema
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Dialog open={duplicateOpen} onClose={() => setDuplicateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Duplicar questionário</DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Origem: {duplicateSource?.title}
          </Typography>
          <TextField
            label="Novo título"
            value={duplicateForm.title}
            onChange={(e) => setDuplicateForm((p) => ({ ...p, title: e.target.value }))}
            fullWidth
          />
          <TextField
            label="Novo slug"
            value={duplicateForm.slug}
            onChange={(e) => setDuplicateForm((p) => ({ ...p, slug: e.target.value }))}
            fullWidth
          />
          <TextField
            label="Ano letivo"
            value={duplicateForm.school_year}
            onChange={(e) => setDuplicateForm((p) => ({ ...p, school_year: e.target.value }))}
            fullWidth
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDuplicateOpen(false)}>Cancelar</Button>
          <Button variant="contained" disabled={saving} onClick={handleDuplicate}>
            Duplicar
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
